import { parseSessionCalendarDate } from '@/components/chat/utils/parseSessionCalendarDate';
import { TypographyP2, TypographyCaption } from '@heidi/design-system/typography';
import { parseSessionDateRange, useInvokeOnce } from '@heidi/utils';
import { cn } from '@/lib/cn';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import { useTranslations, type TranslationKeys } from '@/utils/translations';
import { format, isSameDay } from 'date-fns';
import {
  Calendar,
  ChevronDown,
  Clock,
  Loader2,
  AlertCircle,
  CalendarX,
  ChevronRight,
} from 'lucide-react';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import sanitizeHtml from 'sanitize-html';
import type { SessionByDateItem } from '@heidi/session/contracts';

/** Strip HTML tags from a string and collapse whitespace */
const stripHtml = (html: string): string => {
  const text = sanitizeHtml(html, { allowedTags: [] });
  return text.replace(/\s+/g, ' ').trim();
};

// Above this many sessions the list scrolls instead of growing unbounded,
// keeping the block to roughly five rows tall.
const MAX_VISIBLE_SESSIONS = 5;

interface SessionsByDateBlockProps {
  status: 'fetching' | 'completed' | 'error';
  date: string;
  endDate?: string;
  totalSessions?: number;
  hasMore?: boolean; // True if more sessions exist beyond the returned set
  sessions?: SessionByDateItem[];
  error?: string;
  onBlockError: (blockType: 'sessions_by_date', error?: string) => void;
  className?: string;
  onOpenSession?: (sessionId: string) => void;
}

/**
 * SessionsByDateBlock - Calendar-inspired display of sessions for a specific date
 *
 * Visual states:
 * - FETCHING: Skeleton loading with pulse animation
 * - COMPLETED: Calendar tile + timeline of sessions
 * - ERROR: Error message with retry hint
 */
export const SessionsByDateBlock = ({
  status,
  date,
  endDate,
  totalSessions,
  hasMore,
  sessions,
  error,
  onBlockError,
  className,
  onOpenSession,
}: SessionsByDateBlockProps): JSX.Element => {
  const router = useRouter();
  const intl = useIntl();
  const { t, locale } = useTranslations();
  const [isOpen, setIsOpen] = useState(true);
  useInvokeOnce(() => onBlockError('sessions_by_date', error), status === 'error');

  const handleSessionClick = (sessionId: string) => {
    if (onOpenSession) {
      onOpenSession(sessionId);
      return;
    }

    router.push(`/scribe?sessionId=${sessionId}`);
  };

  const userLocale = locale || undefined; // undefined uses browser default

  // Derive the actual date range from the sessions themselves so we never
  // rely on an empty / incorrect `date` prop (which falls back to "today").
  const dateRange = useMemo(() => {
    if (!sessions?.length) {
      const parsedRange = parseSessionDateRange(date);
      const startInput = parsedRange?.startDate ?? date;
      const endInput = endDate ?? parsedRange?.endDate;
      const fallback =
        parseSessionCalendarDate(startInput) ??
        parseTimeStamp(startInput || undefined, true);
      const fallbackEnd =
        endInput ?
          (parseSessionCalendarDate(endInput) ?? parseTimeStamp(endInput, true))
        : fallback;
      return {
        start: fallback,
        end: fallbackEnd,
        isRange: !isSameDay(fallback, fallbackEnd),
      };
    }

    const parsed = sessions.map((s) => parseTimeStamp(s.session_date, true));
    const sorted = [...parsed].sort((a, b) => a.getTime() - b.getTime());
    const start = sorted[0]!;
    const end = sorted[sorted.length - 1]!;

    return { start, end, isRange: !isSameDay(start, end) };
  }, [sessions, date, endDate]);

  // Helper: build display parts for a single date
  const getDateParts = (dateObj: Date) => {
    const now = new Date();
    return {
      dayNum: dateObj.getDate(),
      dayName: dateObj.toLocaleDateString(userLocale, { weekday: 'short' }),
      monthName: dateObj.toLocaleDateString(userLocale, { month: 'short' }),
      year: dateObj.getFullYear(),
      isToday: now.toDateString() === dateObj.toDateString(),
      isYesterday:
        new Date(Date.now() - 86400000).toDateString() === dateObj.toDateString(),
    };
  };

  // Format session time/date depending on whether sessions span multiple days
  const formatSessionDate = (dateStr: string): string => {
    const dateObj = parseTimeStamp(dateStr, true);
    if (dateRange.isRange) {
      // Multi-day: show abbreviated date + time (e.g. "Jan 5, 11:17 AM")
      return format(dateObj, 'MMM d, h:mm a');
    }
    // Single day: just show the time
    return format(dateObj, 'h:mm a');
  };

  // Get status color
  const getStatusStyle = (sessionStatus?: string) => {
    const status = sessionStatus?.toLowerCase();
    if (status === 'completed' || status === 'complete') {
      return 'bg-validation-success-muted text-validation-success';
    }
    if (status === 'in_progress' || status === 'processing') {
      return 'bg-validation-warning-muted text-validation-warning';
    }
    if (status === 'empty') {
      return 'bg-surface-disabled text-text-disabled';
    }
    return 'bg-validation-info-muted text-validation-info';
  };

  // Build display label for the header
  const startParts = getDateParts(dateRange.start);
  const endParts = dateRange.isRange ? getDateParts(dateRange.end) : startParts;

  const headerLabel = (() => {
    if (!dateRange.isRange) {
      // Single-day display
      if (startParts.isToday) return t('chat.sessionsByDate.today' as TranslationKeys);
      if (startParts.isYesterday)
        return t('chat.sessionsByDate.yesterday' as TranslationKeys);
      return `${startParts.dayName}, ${startParts.monthName} ${startParts.dayNum}`;
    }
    // Multi-day range label (e.g. "Jan 5 – Jan 28" or "Dec 28 – Jan 5")
    const startLabel = `${startParts.monthName} ${startParts.dayNum}`;
    const endLabel = `${endParts.monthName} ${endParts.dayNum}`;
    return `${startLabel} – ${endLabel}`;
  })();

  const emptyStateDescription = (() => {
    if (dateRange.isRange) {
      return intl.formatMessage(
        {
          id: 'Qn70mEDXWK',
          defaultMessage:
            'No sessions from {startMonthName} {startDayNum} to {endMonthName} {endDayNum}',
          description: 'Empty state for a date range with no recorded sessions.',
        },
        {
          startMonthName: startParts.monthName,
          startDayNum: startParts.dayNum,
          endMonthName: endParts.monthName,
          endDayNum: endParts.dayNum,
        }
      );
    }

    if (startParts.isToday) {
      return t('chat.sessionsByDate.noSessionsToday' as TranslationKeys);
    }

    if (startParts.isYesterday) {
      return t('chat.sessionsByDate.noSessionsYesterday' as TranslationKeys);
    }

    return t('chat.sessionsByDate.noSessionsOnDate' as TranslationKeys, {
      dayName: startParts.dayName,
      monthName: startParts.monthName,
      dayNum: startParts.dayNum,
    });
  })();

  // Fetching state
  if (status === 'fetching') {
    return (
      <div
        className={cn(
          'flex w-full gap-4 rounded-xl border border-border/50 bg-surface p-4 duration-300 animate-in fade-in',
          className
        )}
      >
        {/* Calendar tile skeleton */}
        <div className="flex size-16 shrink-0 animate-pulse flex-col items-center justify-center rounded-lg bg-background-secondary">
          <div className="mb-1 h-3 w-8 rounded bg-border/50" />
          <div className="h-6 w-6 rounded bg-border/50" />
        </div>
        {/* Content skeleton */}
        <div className="flex flex-1 flex-col gap-3">
          <div className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin text-brand-primary" />
            <TypographyP2 className="text-text-secondary">
              {t('chat.sessionsByDate.loading' as TranslationKeys)}
            </TypographyP2>
          </div>
          <div className="space-y-2">
            <div className="h-12 animate-pulse rounded-lg bg-background-secondary" />
            <div className="h-12 animate-pulse rounded-lg bg-background-secondary" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full gap-4 rounded-xl border border-destructive/20 bg-destructive/5 p-4 duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex size-16 shrink-0 flex-col items-center justify-center rounded-lg bg-destructive/10">
          <AlertCircle className="size-6 text-destructive" />
        </div>
        <div className="flex flex-1 flex-col justify-center gap-1">
          <TypographyP2 className="font-medium text-text-primary">
            {t('chat.sessionsByDate.errorTitle' as TranslationKeys)}
          </TypographyP2>
          <TypographyCaption className="text-destructive">
            {error || t('chat.sessionsByDate.errorDefault' as TranslationKeys)}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  // Empty state
  if (!sessions || sessions.length === 0) {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-border/50 bg-surface px-4 py-3 duration-300 animate-in fade-in',
          className
        )}
      >
        {/* Calendar tile */}
        <div className="flex size-12 shrink-0 flex-col items-center justify-center rounded-lg border border-border/50 bg-background-secondary">
          <span className="text-[9px] font-semibold uppercase tracking-wider leading-none text-text-tertiary">
            {startParts.monthName}
          </span>
          <span className="text-lg font-bold tabular-nums leading-tight text-text-secondary">
            {startParts.dayNum}
          </span>
        </div>
        <div className="flex flex-1 flex-col justify-center gap-0.5">
          <div className="flex items-center gap-2">
            <CalendarX className="size-4 text-text-tertiary" />
            <TypographyP2 className="font-medium text-text-primary">
              {t('chat.sessionsByDate.noConsultations' as TranslationKeys)}
            </TypographyP2>
          </div>
          <TypographyCaption className="text-text-tertiary">
            {emptyStateDescription}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  // Completed state with sessions
  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-xl border border-border/50 bg-surface duration-300 animate-in fade-in',
        className
      )}
    >
      {/* Accordion header — clickable */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'flex w-full items-center gap-3 bg-background-secondary/50 px-4 py-3 text-left transition-colors hover:bg-background-secondary/80',
          isOpen && 'border-b border-border/40'
        )}
      >
        {/* Calendar tile — single-day shows day number, multi-day shows calendar icon */}
        {dateRange.isRange ? (
          <div className="flex size-12 shrink-0 flex-col items-center justify-center rounded-lg border border-border/50 bg-surface">
            <Calendar className="size-5 text-text-secondary" />
          </div>
        ) : (
          <div
            className={cn(
              'flex size-12 shrink-0 flex-col items-center justify-center rounded-lg border',
              startParts.isToday
                ? 'border-brand-primary/30 bg-brand-primary/10'
                : 'border-border/50 bg-surface'
            )}
          >
            <span
              className={cn(
                'text-[9px] font-semibold uppercase tracking-wider leading-none',
                startParts.isToday ? 'text-brand-primary' : 'text-text-tertiary'
              )}
            >
              {startParts.monthName}
            </span>
            <span
              className={cn(
                'text-lg font-bold tabular-nums leading-tight',
                startParts.isToday ? 'text-brand-primary' : 'text-text-primary'
              )}
            >
              {startParts.dayNum}
            </span>
          </div>
        )}

        {/* Date label + session count */}
        <div className="flex flex-1 flex-col">
          <span className="text-sm font-semibold text-text-primary">
            {headerLabel}
          </span>
          <TypographyCaption className="text-text-tertiary">
            {t('chat.sessionsByDate.sessionCount' as TranslationKeys, { count: totalSessions })}
          </TypographyCaption>
        </div>

        {/* Chevron indicator */}
        <ChevronDown
          className={cn(
            'size-5 shrink-0 text-text-tertiary transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Collapsible body — uses CSS grid row transition for smooth animation */}
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          {/* Sessions list — cap to ~5 rows and scroll when there are more */}
          <div
            className={cn(
              'flex flex-col divide-y divide-border/30',
              sessions.length > MAX_VISIBLE_SESSIONS && 'max-h-[21rem] overflow-y-auto'
            )}
          >
            {sessions.map((session) => {
              const notePreview = session.heidi_output
                ? stripHtml(session.heidi_output)
                : '';

              return (
                <button
                  type="button"
                  key={session.session_id}
                  onClick={() => handleSessionClick(session.session_id)}
                  className="group flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover"
                >
                  {/* Time / date column */}
                  <div
                    className={cn(
                      'flex shrink-0 flex-col items-center gap-0.5',
                      dateRange.isRange ? 'w-[5.5rem]' : 'w-14'
                    )}
                  >
                    <Clock className="size-3.5 text-text-tertiary group-hover:text-brand-primary" />
                    <span className="text-[11px] font-medium tabular-nums text-text-tertiary text-center">
                      {formatSessionDate(session.session_date)}
                    </span>
                  </div>

                  {/* Session content */}
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 truncate text-sm font-medium text-text-primary">
                        {session.patient_name ||
                          session.patient_demographics ||
                          t('chat.sessionsByDate.unknownPatient' as TranslationKeys)}
                      </span>
                      {session.session_status && (
                        <span
                          className={cn(
                            'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize leading-none',
                            getStatusStyle(session.session_status)
                          )}
                        >
                          {session.session_status.toLowerCase().replace('_', ' ')}
                        </span>
                      )}
                    </div>

                    {/* Duration */}
                    {session.duration_minutes != null && session.duration_minutes > 0 && (
                      <TypographyCaption className="text-text-tertiary">
                        {t('chat.sessionsByDate.minConsultation' as TranslationKeys, {
                          minutes: session.duration_minutes,
                        })}
                      </TypographyCaption>
                    )}

                    {/* Note preview — HTML stripped */}
                    {notePreview && (
                      <TypographyCaption className="line-clamp-1 text-text-tertiary">
                        {notePreview.slice(0, 120)}
                        {notePreview.length > 120 ? '...' : ''}
                      </TypographyCaption>
                    )}
                  </div>

                  {/* Arrow indicator */}
                  <ChevronRight className="size-4 shrink-0 text-text-tertiary opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5" />
                </button>
              );
            })}
          </div>

          {/* Has more indicator */}
          {hasMore && (
            <div className="border-t border-border/30 px-4 py-2.5">
              <TypographyCaption className="text-center text-text-tertiary italic">
                {t('chat.sessionsByDate.showingMore' as TranslationKeys, { count: sessions.length })}
              </TypographyCaption>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
